import { supabase, handleSupabaseError, getCurrentUser } from '@/lib/supabase'

// Core integration utilities
export const Core = {
  // Health check for the API
  async healthCheck() {
    try {
      const { data, error } = await supabase.from('camp_audit_data').select('count').limit(1)
      if (error) throw error
      return { status: 'healthy', timestamp: new Date().toISOString() }
    } catch (error) {
      return { status: 'error', error: error.message, timestamp: new Date().toISOString() }
    }
  },

  // Get app configuration
  async getConfig() {
    try {
      const { data, error } = await supabase
        .from('app_config')
        .select('*')
        .single()
      
      if (error && error.code !== 'PGRST116') throw error
      return data || {}
    } catch (error) {
      handleSupabaseError(error)
    }
  }
};

// AI/LLM Integration using Supabase Edge Functions with timeout
export const InvokeLLM = async (prompt, options = {}) => {
  try {
    const user = await getCurrentUser()
    if (!user) throw new Error('User not authenticated')

    // Set default timeout to 15 seconds for LLM calls
    const timeoutMs = options.timeout || 15000;

    // Create timeout promise
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('LLM request timeout - taking too long')), timeoutMs)
    );

    // Create the actual LLM request promise
    const llmPromise = supabase.functions.invoke('invoke-llm', {
      body: {
        prompt,
        user_id: user.id,
        ...options
      }
    });

    // Race between LLM call and timeout
    const { data, error } = await Promise.race([llmPromise, timeoutPromise]);

    if (error) throw error
    return data
  } catch (error) {
    handleSupabaseError(error)
  }
};

// Email integration
export const SendEmail = async (emailData) => {
  try {
    const user = await getCurrentUser()
    if (!user) throw new Error('User not authenticated')

    const { data, error } = await supabase.functions.invoke('send-email', {
      body: {
        ...emailData,
        from_user_id: user.id
      }
    })
    
    if (error) {
      // Check if it's a non-2xx status code error
      if (error.message && error.message.includes('non-2xx')) {
        // Try to extract more details from the error
        let errorDetails = error.message
        
        // Try to get response body if available
        if (error.context) {
          try {
            // If context is a Response object, try to read it
            if (error.context instanceof Response || (error.context && typeof error.context.text === 'function')) {
              try {
                const text = await error.context.text()
                const json = JSON.parse(text)
                errorDetails = json.error || json.details || text
              } catch (e) {
                // If parsing fails, use the status
                errorDetails = `Status ${error.context.status || 'unknown'}: ${error.context.statusText || 'Error'}`
              }
            } else if (typeof error.context === 'string') {
              errorDetails = error.context
            } else if (error.context.error) {
              errorDetails = error.context.error + (error.context.details ? ': ' + error.context.details : '')
            }
          } catch (e) {
            // If we can't parse, use what we have
            errorDetails = error.context.toString ? error.context.toString() : String(error.context)
          }
        } else if (error.details) {
          errorDetails = error.details
        }
        
        throw new Error(`Edge Function returned a non-2xx status code: ${errorDetails}`)
      }
      throw error
    }
    
    // Check if data indicates an error
    if (data && data.error) {
      throw new Error(data.error + (data.details ? ': ' + data.details : ''))
    }
    
    return data
  } catch (error) {
    // Re-throw with more context if needed
    if (error.message && !error.message.includes('Edge Function')) {
      console.error('SendEmail error:', error)
    }
    handleSupabaseError(error)
  }
};

// Stripe checkout session creation
export const CreateStripeCheckoutSession = async (planCode, successUrl, cancelUrl) => {
  try {
    const user = await getCurrentUser()
    if (!user) throw new Error('User not authenticated')

    console.log('Creating checkout session for plan:', planCode)

    // Use current origin if URLs not provided
    const currentOrigin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5173'
    const finalSuccessUrl = successUrl || `${currentOrigin}/premium?success=true`
    const finalCancelUrl = cancelUrl || `${currentOrigin}/premium?canceled=true`

    const { data, error } = await supabase.functions.invoke('create-checkout-session', {
      body: {
        planCode,
        successUrl: finalSuccessUrl,
        cancelUrl: finalCancelUrl
      }
    })

    if (error) {
      console.error('Edge Function error:', error)
      // Extract more details from error if available
      const errorMessage = error.message || 'Failed to create checkout session'
      const errorDetails = error.details || error.context || ''
      throw new Error(`${errorMessage}${errorDetails ? ': ' + errorDetails : ''}`)
    }

    console.log('Checkout session created:', data)
    return data
  } catch (error) {
    console.error('CreateStripeCheckoutSession error:', error)
    handleSupabaseError(error)
  }
};

// Get Stripe subscription status
export const GetStripeSubscriptionStatus = async () => {
  try {
    const user = await getCurrentUser()
    if (!user) throw new Error('User not authenticated')

    const { data, error } = await supabase.functions.invoke('get-subscription-status', {
      body: {}
    })

    if (error) {
      console.error('Edge Function error:', error)
      // Return default free status on error
      return {
        status: 'free',
        plan: null,
        trialEndsAt: null,
        currentPeriodEnd: null,
        cancelAtPeriodEnd: false
      }
    }

    return data
  } catch (error) {
    console.error('GetStripeSubscriptionStatus error:', error)
    // Return default free status on error
    return {
      status: 'free',
      plan: null,
      trialEndsAt: null,
      currentPeriodEnd: null,
      cancelAtPeriodEnd: false
    }
  }
};

// Create Stripe customer portal session
export const CreateStripePortalSession = async (returnUrl) => {
  try {
    const user = await getCurrentUser()
    if (!user) throw new Error('User not authenticated')

    const { data, error } = await supabase.functions.invoke('create-portal-session', {
      body: {
        returnUrl
      }
    })

    if (error) {
      console.error('Edge Function error:', error)
      const errorMessage = error.message || 'Failed to create portal session'
      const errorDetails = error.details || error.context || ''
      throw new Error(`${errorMessage}${errorDetails ? ': ' + errorDetails : ''}`)
    }

    return data
  } catch (error) {
    console.error('CreateStripePortalSession error:', error)
    handleSupabaseError(error)
  }
};

// Cancel Stripe subscription with feedback
export const CancelStripeSubscription = async (reasons, additionalFeedback = null) => {
  try {
    const user = await getCurrentUser()
    if (!user) throw new Error('User not authenticated')

    console.log('Canceling subscription with feedback:', reasons)

    const { data, error } = await supabase.functions.invoke('cancel-subscription', {
      body: {
        reasons,
        additionalFeedback
      }
    })

    if (error) {
      console.error('Edge Function error:', error)
      const errorMessage = error.message || 'Failed to cancel subscription'
      const errorDetails = error.details || error.context || ''
      throw new Error(`${errorMessage}${errorDetails ? ': ' + errorDetails : ''}`)
    }

    console.log('Subscription canceled:', data)
    return data
  } catch (error) {
    console.error('CancelStripeSubscription error:', error)
    handleSupabaseError(error)
  }
};

// Change Stripe subscription plan
export const ChangeStripeSubscriptionPlan = async (newPlanCode) => {
  try {
    const user = await getCurrentUser()
    if (!user) throw new Error('User not authenticated')

    console.log('Changing subscription plan to:', newPlanCode)

    const { data, error } = await supabase.functions.invoke('change-subscription-plan', {
      body: {
        newPlanCode
      }
    })

    if (error) {
      console.error('Edge Function error:', error)
      const errorMessage = error.message || 'Failed to change subscription plan'
      const errorDetails = error.details || error.context || ''
      throw new Error(`${errorMessage}${errorDetails ? ': ' + errorDetails : ''}`)
    }

    console.log('Subscription plan changed:', data)
    return data
  } catch (error) {
    console.error('ChangeStripeSubscriptionPlan error:', error)
    handleSupabaseError(error)
  }
};

// File upload to Supabase Storage
export const UploadFile = async (file, bucket = 'uploads', path = null) => {
  try {
    const user = await getCurrentUser()
    if (!user) throw new Error('User not authenticated')

    const fileName = path || `${user.id}/${Date.now()}_${file.name}`
    
    const { data, error } = await supabase.storage
      .from(bucket)
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false
      })
    
    if (error) throw error

    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from(bucket)
      .getPublicUrl(fileName)
    
    return {
      path: data.path,
      fullPath: data.fullPath,
      publicUrl: publicUrl
    }
  } catch (error) {
    handleSupabaseError(error)
  }
};

// Image generation using AI (Edge Function)
export const GenerateImage = async (prompt, options = {}) => {
  try {
    const user = await getCurrentUser()
    if (!user) throw new Error('User not authenticated')

    const { data, error } = await supabase.functions.invoke('generate-image', {
      body: {
        prompt,
        user_id: user.id,
        ...options
      }
    })
    
    if (error) throw error
    return data
  } catch (error) {
    handleSupabaseError(error)
  }
};

// Extract data from uploaded files using AI
export const ExtractDataFromUploadedFile = async (filePath, extractionType = 'general') => {
  try {
    const user = await getCurrentUser()
    if (!user) throw new Error('User not authenticated')

    const { data, error } = await supabase.functions.invoke('extract-file-data', {
      body: {
        file_path: filePath,
        extraction_type: extractionType,
        user_id: user.id
      }
    })
    
    if (error) throw error
    return data
  } catch (error) {
    handleSupabaseError(error)
  }
};

// Create signed URL for secure file access
export const CreateFileSignedUrl = async (filePath, bucket = 'uploads', expiresIn = 3600) => {
  try {
    const { data, error } = await supabase.storage
      .from(bucket)
      .createSignedUrl(filePath, expiresIn)
    
    if (error) throw error
    return data
  } catch (error) {
    handleSupabaseError(error)
  }
};

// Upload private files with restricted access
export const UploadPrivateFile = async (file, bucket = 'private', path = null) => {
  try {
    const user = await getCurrentUser()
    if (!user) throw new Error('User not authenticated')

    const fileName = path || `${user.id}/private/${Date.now()}_${file.name}`
    
    const { data, error } = await supabase.storage
      .from(bucket)
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false
      })
    
    if (error) throw error

    // Create signed URL for private access
    const signedUrl = await CreateFileSignedUrl(fileName, bucket)
    
    return {
      path: data.path,
      fullPath: data.fullPath,
      signedUrl: signedUrl.signedUrl
    }
  } catch (error) {
    handleSupabaseError(error)
  }
};

// Additional utility functions
export const GetUserFiles = async (bucket = 'uploads') => {
  try {
    const user = await getCurrentUser()
    if (!user) throw new Error('User not authenticated')

    const { data, error } = await supabase.storage
      .from(bucket)
      .list(user.id, {
        limit: 100,
        offset: 0
      })
    
    if (error) throw error
    return data || []
  } catch (error) {
    handleSupabaseError(error)
  }
};

export const DeleteFile = async (filePath, bucket = 'uploads') => {
  try {
    const user = await getCurrentUser()
    if (!user) throw new Error('User not authenticated')

    // Ensure user can only delete their own files
    if (!filePath.startsWith(user.id)) {
      throw new Error('Unauthorized file access')
    }

    const { data, error } = await supabase.storage
      .from(bucket)
      .remove([filePath])
    
    if (error) throw error
    return data
  } catch (error) {
    handleSupabaseError(error)
  }
};